"use client"

import * as React from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm } from '@open-mercato/ui/backend/CrudForm'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { COMPLIANCE_DOCUMENT_ENTITY_ID } from '../../../lib/complianceDocument'
import {
  buildComplianceDocumentCreatePayload,
  createComplianceDocumentFormFields,
  createComplianceDocumentFormGroups,
  createComplianceDocumentFormSchema,
  createComplianceDocumentInitialValues,
  type ComplianceDocumentFormValues,
} from '../../../components/formConfig'

export default function CreateComplianceDocumentPage() {
  const t = useT()
  const router = useRouter()
  const searchParams = useSearchParams()
  const preselectedParticipantId = React.useMemo(() => {
    const value = searchParams?.get('participantProfileId')
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
  }, [searchParams])

  const formSchema = React.useMemo(() => createComplianceDocumentFormSchema(), [])
  const fields = React.useMemo(
    () => createComplianceDocumentFormFields(t, { lockedParticipantId: preselectedParticipantId }),
    [t, preselectedParticipantId],
  )
  const groups = React.useMemo(() => createComplianceDocumentFormGroups(t), [t])
  const initialValues = React.useMemo(
    () => createComplianceDocumentInitialValues(preselectedParticipantId),
    [preselectedParticipantId],
  )

  return (
    <Page>
      <PageBody>
        <CrudForm<ComplianceDocumentFormValues>
          key={preselectedParticipantId ?? 'new'}
          title={t('compliance.create.title', 'Create compliance document')}
          backHref="/backend/compliance-documents"
          cancelHref="/backend/compliance-documents"
          submitLabel={t('compliance.form.submitCreate', 'Create document')}
          schema={formSchema}
          fields={fields}
          groups={groups}
          entityId={COMPLIANCE_DOCUMENT_ENTITY_ID}
          initialValues={initialValues}
          onSubmit={async (values) => {
            const payload = buildComplianceDocumentCreatePayload(values)
            const response = await createCrud<{ id?: string }>('compliance/documents', payload)
            flash(t('compliance.actions.created', 'Compliance document created.'), 'success')
            const createdId = typeof response.result?.id === 'string' ? response.result.id : null
            if (createdId) {
              router.push(`/backend/compliance-documents/${createdId}`)
              return
            }
            router.push('/backend/compliance-documents')
          }}
        />
      </PageBody>
    </Page>
  )
}
